import { keys, startCase } from 'lodash'
import { formatDate } from 'app/date.utils'

/**
 * What one field change on a contact amounts to, in words.
 *
 * Extracted from the Change card because two surfaces now render the same
 * facts: a single change activity as a sentence, and a month's worth of them
 * as rows in one grouped card. The field-by-field rules (dates, owners,
 * pipeline moves, the boolean that reads as a status) are the substance, and
 * having them in two places would let the two surfaces describe the same
 * record differently.
 */
export type ChangeLine =
  /** A value moved from one thing to another. */
  | { kind: `transition`; field: string; from?: string; to?: string }
  /** A change with no meaningful "from", e.g. a pipeline move. */
  | { kind: `statement`; field: string; text: string }

const FIELD_DISPLAY_NAMES: Record<string, string> = {
  leadOwner: `Contact Owner`,
}

const toPipelineArray = (value: unknown): { id?: string; stage?: string }[] =>
  Array.isArray(value) ? value : value ? [value as { id?: string; stage?: string }] : []

/**
 * Pipelines are stored as the whole array before and after, so the interesting
 * fact has to be recovered by diffing: what was added, dropped, or moved.
 * Returns undefined when nothing actually moved, so the caller renders nothing
 * rather than an empty sentence.
 */
function pipelineText(value: { from?: unknown; to?: unknown }): string | undefined {
  const from = toPipelineArray(value.from)
  const to = toPipelineArray(value.to)

  const added = to.filter((t) => !from.some((f) => f.id === t.id))
  if (added.length) return `added to stage ${added[0].stage}`

  const removed = from.filter((f) => !to.some((t) => t.id === f.id))
  if (removed.length) return `removed from stage ${removed[0].stage}`

  const moved = to.filter((t) => from.some((f) => f.id === t.id && f.stage !== t.stage))
  if (moved.length) {
    const fromStage = from.find((f) => f.id === moved[0].id)?.stage
    return `moved from ${fromStage} to ${moved[0].stage}`
  }

  return undefined
}

const asText = (value: unknown): string | undefined => {
  if (Array.isArray(value)) return value.map((entry) => startCase(String(entry))).join(`, `)
  return value ? startCase(String(value)) : undefined
}

/**
 * Describes a `{ field: { from, to } }` pair. Returns undefined when the change
 * carries nothing worth showing.
 */
export function describeChange(change: Record<string, any>): ChangeLine | undefined {
  const field = keys(change)?.[0]
  if (!field) return undefined
  const value = change[field] ?? {}
  const displayField = FIELD_DISPLAY_NAMES[field] || startCase(field)

  if (/date/i.test(field)) {
    return {
      kind: `transition`,
      field: displayField,
      from: formatDate({ value: value.from }),
      to: formatDate({ value: value.to }),
    }
  }

  if (/owner/i.test(field)) {
    return {
      kind: `transition`,
      field: displayField,
      from: startCase(value?.from?.name || `No owner`),
      to: startCase(value?.to?.name || `No owner`),
    }
  }

  if (field === `isQualified`) {
    const read = (raw: unknown) => (raw === true ? `Qualified` : raw === false ? `Not Qualified` : `Blank`)
    return { kind: `transition`, field: displayField, from: read(value?.from), to: read(value?.to) }
  }

  if (field === `pipelines`) {
    const text = pipelineText(value)
    return text ? { kind: `statement`, field: displayField, text } : undefined
  }

  return { kind: `transition`, field: displayField, from: asText(value.from), to: asText(value.to) }
}

/** The card's sentence form: "Lead Status changed from "A" to "B" by you". */
export function changeSentence(line: ChangeLine, source?: string): string {
  if (line.kind === `statement`) return `${line.text} by ${source}`
  return `changed from "${line.from || `empty`}" to "${line.to || `empty`}" by ${source}`
}

type ChangeAuthor = { userId: string } | { label: string }

/**
 * Who a change activity's `source` says made it. Writers disagree on the shape:
 * `User:<id>`, `user <name>`, `Workflow: <name>`, `Appointment:<pms id>`, or a
 * bare kind like `System`. Ids mean nothing to a reader, so only the kind survives
 * them, and a source that is nothing but an id is dropped.
 */
export function changeAuthor(source?: string | null): ChangeAuthor | undefined {
  if (!source) return undefined

  const userId = source.match(/^User:(\S+)$/)?.[1]
  if (userId) return { userId }

  const userName = source.match(/^user (.+)$/)?.[1]
  if (userName) return { label: userName }

  const [, kind, detail] = source.match(/^([A-Za-z]+):\s*(.+)$/) ?? []
  if (kind) return { label: /\s/.test(detail) ? `${kind.toLowerCase()} ${detail}` : startCase(kind).toLowerCase() }

  if (/\d/.test(source) && !/\s/.test(source)) return undefined
  return { label: startCase(source).toLowerCase() }
}

/**
 * Field changes are recorded as one object per activity, so a single "Change"
 * row can hold several. `neededAction` is internal bookkeeping and is hidden
 * from everyone but super users.
 */
export function changeLines(data: Record<string, any>, { includeNeededAction = false } = {}): ChangeLine[] {
  return Object.entries(data)
    .filter(([key]) => includeNeededAction || key !== `neededAction`)
    .map(([key, value]) => describeChange({ [key]: value }))
    .filter((line): line is ChangeLine => !!line)
}
